<template>
  <div class="mx-report-page-root">
    <div
      class="appointment-report mx-report-page"
      :class="{ 'mx-report-page--done': isDone }"
    >
      <div
        class="mx-report-page__column"
        :aria-busy="isLoadingView ? 'true' : null"
      >
        <!-- Cabeçalho: voltar, H1 + chip do tipo, stepper do Figma -->
        <div class="mx-report-page__header">
          <div class="mx-report-page__heading">
            <router-link
              :to="backTo"
              class="ar-link mx-report-page__back"
            >
              <feather-icon
                icon="ArrowLeftIcon"
                size="16"
                aria-hidden="true"
              />
              {{ backLabel }}
            </router-link>
            <div class="mx-report-page__title-row">
              <!-- Recebe o foco ao seguir para a próxima pendência (o conteúdo da tela troca inteiro). -->
              <h1
                ref="pageTitle"
                tabindex="-1"
                class="mx-report-page__title ar-focus-target"
              >
                {{ FIGMA_COPY.pageTitle }}
              </h1>
              <span
                v-if="typeChip"
                class="ar-chip"
                :class="`ar-chip--${typeChip.tone}`"
              >{{ typeChip.label }}</span>
            </div>
          </div>
          <report-stepper
            v-if="showStepper"
            class="mx-report-page__stepper"
            :current="1"
            :state="stepperState"
          />
        </div>

        <!-- Aberto pela central (ou pela trava): "Pendência N de M" + progresso da visita -->
        <section
          v-if="showPendencyBar"
          class="mx-report-page__context"
          :aria-label="A11Y_COPY.pendenciesContext"
        >
          <span
            class="mx-report-page__context-icon"
            aria-hidden="true"
          >
            <feather-icon
              icon="LockIcon"
              size="16"
            />
          </span>
          <span class="mx-report-page__context-position ar-tabular">
            {{ NEW_COPY.pendencyPosition(pendencyIndex + 1, pendencyItems.length) }}
          </span>
          <span
            class="ar-progress mx-report-page__segments"
            aria-hidden="true"
          >
            <span
              v-for="segment in pendencySegments"
              :key="segment.key"
              class="ar-progress__seg"
              :class="segment.ratio === undefined ? `ar-progress__seg--${segment.state}` : 'mx-report-page__seg-ratio'"
              :style="segment.ratio === undefined ? null : { '--mx-fill': segment.ratio }"
            />
          </span>
          <span class="mx-report-page__context-caption">{{ NEW_COPY.lockProgressCaption }}</span>
        </section>

        <!-- Formulário (carregando / aguardando horário / preenchendo / finalizando / erro) -->
        <template v-if="showForm">
          <report-appointment-card
            :appointment="mxReport.appointment"
            :loading="isLoadingView"
          />

          <report-date-lock-banner
            v-if="mxShowDateLock"
            v-bind="mxDateLockProps"
            v-on="dateLockListeners"
          />

          <div
            v-if="isLoadingView"
            class="ar-questions mx-report-page__skeleton"
            aria-hidden="true"
          >
            <div
              v-for="row in SKELETON_ROWS"
              :key="row"
              class="mx-report-page__skeleton-row"
            >
              <span class="mx-report-page__skeleton-legend" />
              <span class="mx-report-page__skeleton-control" />
            </div>
          </div>
          <div
            v-else
            class="ar-questions"
          >
            <div class="ar-classification">
              <report-reason-select
                :ref="mxItemRef(REPORT_CLASSIFICATION)"
                v-bind="mxClassificationProps()"
                v-on="mxClassificationListeners()"
              />
              <p class="ar-classification__help">
                {{ REPORT_CLASSIFICATION_COPY.help }}
              </p>
            </div>
            <report-yes-no
              v-for="key in mxVisibleQuestions"
              :key="key"
              :ref="mxItemRef(key)"
              v-bind="mxYesNoProps(key)"
              v-on="mxListeners(key)"
            >
              <template #follow-up>
                <report-reason-select
                  v-if="mxFollowUpOf(key)"
                  :ref="mxItemRef(mxFollowUpOf(key))"
                  v-bind="mxReasonProps(mxFollowUpOf(key))"
                  v-on="mxListeners(mxFollowUpOf(key))"
                />
              </template>
            </report-yes-no>
          </div>
        </template>

        <!-- MX Salvo (ou já reportado): confirmação real com resumo e próximos passos -->
        <report-success
          v-else-if="mxShowDone"
          v-bind="mxSuccessProps"
          :reported-by="successReportedBy"
          :reported-at="successReportedAt"
          :notice="successNotice"
        >
          <template #next="{ toggleDetails, detailsOpen, detailsId, hasDetails, detailsLabel }">
            <!-- Ainda há pendências (trava): segue para a próxima, sem avanço automático -->
            <template v-if="showPendencySteps">
              <p class="ar-callout ar-callout--info mx-report-page__next-callout">
                <feather-icon
                  icon="InfoIcon"
                  size="16"
                  aria-hidden="true"
                />
                <span>{{ NEW_COPY.remainingPendencies(remainingPendencies) }}</span>
              </p>
              <p
                v-if="contractDeferred"
                class="ar-callout ar-callout--info mx-report-page__next-callout"
              >
                <feather-icon
                  icon="FilePlusIcon"
                  size="16"
                  aria-hidden="true"
                />
                <span>{{ NEW_COPY.contractAfterPendencies }}</span>
              </p>
              <div class="mx-report-page__next-actions">
                <router-link
                  :to="nextPendencyTo"
                  class="ar-btn ar-btn--primary mx-report-page__next-primary"
                >
                  {{ NEW_COPY.nextPendency }}
                  <feather-icon
                    icon="ArrowRightIcon"
                    size="16"
                    aria-hidden="true"
                  />
                </router-link>
                <router-link
                  :to="hubTo"
                  class="ar-btn ar-btn--ghost"
                >
                  {{ NEW_COPY.allPendencies }}
                </router-link>
                <!-- Trava em `log`/`off`: há pendências, mas nada bloqueia o cadastro do contrato -->
                <button
                  v-if="canRegisterContract"
                  type="button"
                  class="ar-btn ar-btn--secondary"
                  @click="registerContract"
                >
                  <feather-icon
                    icon="FilePlusIcon"
                    size="16"
                    aria-hidden="true"
                  />
                  {{ NEW_COPY.registerContract }}
                </button>
              </div>
            </template>

            <!-- Sem pendências: volta para Compromissos (ou "Continuar", liberado) e, com negócio fechado, o contrato -->
            <template v-else>
              <p
                v-if="showReleasedCallout"
                class="ar-callout ar-callout--success mx-report-page__next-callout"
              >
                <feather-icon
                  icon="UnlockIcon"
                  size="16"
                  aria-hidden="true"
                />
                <span>
                  <strong class="mx-report-page__callout-title">{{ NEW_COPY.unlockedTitle }}</strong>
                  {{ NEW_COPY.unlockedText }}
                </span>
              </p>
              <p
                v-if="contractDeferred"
                class="ar-callout ar-callout--info mx-report-page__next-callout"
              >
                <feather-icon
                  icon="FilePlusIcon"
                  size="16"
                  aria-hidden="true"
                />
                <span>{{ NEW_COPY.contractAfterPendencies }}</span>
              </p>
              <div class="mx-report-page__next-actions">
                <button
                  v-if="canRegisterContract"
                  type="button"
                  class="ar-btn ar-btn--primary mx-report-page__next-primary"
                  @click="registerContract"
                >
                  <feather-icon
                    icon="FilePlusIcon"
                    size="16"
                    aria-hidden="true"
                  />
                  {{ NEW_COPY.registerContract }}
                </button>
                <router-link
                  :to="appointmentsTo"
                  class="ar-btn"
                  :class="canRegisterContract
                    ? 'ar-btn--secondary'
                    : 'ar-btn--primary mx-report-page__next-primary'"
                >
                  {{ leaveLabel }}
                </router-link>
                <button
                  v-if="hasDetails"
                  type="button"
                  class="ar-btn ar-btn--ghost"
                  :aria-expanded="detailsOpen ? 'true' : 'false'"
                  :aria-controls="detailsId"
                  @click="toggleDetails"
                >
                  {{ detailsLabel }}
                  <feather-icon
                    icon="ChevronDownIcon"
                    size="16"
                    class="mx-report-page__chevron"
                    :class="{ 'mx-report-page__chevron--open': detailsOpen }"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </template>
          </template>
        </report-success>

        <!-- Sem formulário: não é o PN Responsável ou o compromisso (Momento X) saiu do funil. Nos demais tipos o
             aviso é do próprio StandardReportForm (abaixo). -->
        <section
          v-else-if="showUnavailable"
          class="ar-card mx-report-page__notice"
        >
          <p class="ar-callout ar-callout--warning">
            <feather-icon
              icon="AlertCircleIcon"
              size="16"
              aria-hidden="true"
            />
            <span>{{ unavailableText }}</span>
          </p>
          <div class="mx-report-page__notice-actions">
            <router-link
              :to="backTo"
              class="ar-btn ar-btn--primary"
            >
              {{ pendencyContext ? NEW_COPY.allPendencies : NEW_COPY.backToAppointments }}
            </router-link>
          </div>
        </section>

        <!-- Falha ao carregar -->
        <section
          v-else-if="showLoadError"
          class="ar-card mx-report-page__notice"
        >
          <div
            role="alert"
            class="ar-callout ar-callout--danger"
          >
            <feather-icon
              icon="AlertTriangleIcon"
              size="20"
              aria-hidden="true"
            />
            <p class="mx-report-page__notice-text">
              {{ APP_COPY.loadReportError }}
            </p>
          </div>
          <div class="mx-report-page__notice-actions">
            <button
              type="button"
              class="ar-btn ar-btn--secondary"
              @click="mxLoad()"
            >
              <feather-icon
                icon="RefreshCwIcon"
                size="16"
                aria-hidden="true"
              />
              {{ NEW_COPY.retry }}
            </button>
          </div>
        </section>
      </div>

      <!-- Entrevista / Parceria / Negociação / recrutamento de PN: as perguntas de sempre, no visual do Report. Fora
           da coluna para a barra fixa ocupar a largura da área de conteúdo, como a do Momento X (o corpo do
           formulário volta para a largura da coluna no CSS). Montado com a resposta que a página já buscou
           (`preloaded`): um carregamento só. -->
      <standard-report-form
        v-if="standardMode && standardSeed"
        :key="standardSeed.key"
        class="mx-report-page__standard"
        :appointment-id="appointmentId"
        :preloaded="standardSeed.body"
        appointment-card
        bar-class="mx-report-page__standard-bar"
        v-on="standardEditListeners"
        @status-change="onStandardStatusChange"
        @reported="onStandardReported"
        @already-reported="onStandardAlreadyReported"
      >
        <template #unavailable-actions>
          <router-link
            :to="backTo"
            class="ar-btn ar-btn--primary"
          >
            {{ pendencyContext ? NEW_COPY.allPendencies : NEW_COPY.backToAppointments }}
          </router-link>
        </template>

        <!-- Próximos passos da página. Enquanto o formulário pergunta pelo contrato da Parceria fechada / pelo
             questionário da entrevista individual (como a sidebar fazia), só essa pergunta aparece. -->
        <template #next="scope">
          <template v-if="!scope.postSave">
            <template v-if="showPendencySteps">
              <p class="ar-callout ar-callout--info mx-report-page__next-callout">
                <feather-icon
                  icon="InfoIcon"
                  size="16"
                  aria-hidden="true"
                />
                <span>{{ NEW_COPY.remainingPendencies(remainingPendencies) }}</span>
              </p>
              <div class="mx-report-page__next-actions">
                <router-link
                  :to="nextPendencyTo"
                  class="ar-btn ar-btn--primary mx-report-page__next-primary"
                >
                  {{ NEW_COPY.nextPendency }}
                  <feather-icon
                    icon="ArrowRightIcon"
                    size="16"
                    aria-hidden="true"
                  />
                </router-link>
                <router-link
                  :to="hubTo"
                  class="ar-btn ar-btn--ghost"
                >
                  {{ NEW_COPY.allPendencies }}
                </router-link>
              </div>
            </template>
            <template v-else>
              <p
                v-if="showReleasedCallout"
                class="ar-callout ar-callout--success mx-report-page__next-callout"
              >
                <feather-icon
                  icon="UnlockIcon"
                  size="16"
                  aria-hidden="true"
                />
                <span>
                  <strong class="mx-report-page__callout-title">{{ NEW_COPY.unlockedTitle }}</strong>
                  {{ NEW_COPY.unlockedText }}
                </span>
              </p>
              <div class="mx-report-page__next-actions">
                <router-link
                  :to="appointmentsTo"
                  class="ar-btn ar-btn--primary mx-report-page__next-primary"
                >
                  {{ leaveLabel }}
                </router-link>
                <button
                  v-if="scope.hasDetails"
                  type="button"
                  class="ar-btn ar-btn--ghost"
                  :aria-expanded="scope.detailsOpen ? 'true' : 'false'"
                  :aria-controls="scope.detailsId"
                  @click="scope.toggleDetails"
                >
                  {{ scope.detailsLabel }}
                  <feather-icon
                    icon="ChevronDownIcon"
                    size="16"
                    class="mx-report-page__chevron"
                    :class="{ 'mx-report-page__chevron--open': scope.detailsOpen }"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </template>
          </template>
        </template>
      </standard-report-form>

      <!-- Barra fixa: status + autosave + "Finalizar Momento X" (erro acima dela, role="alert") -->
      <report-action-bar
        v-if="showActionBar"
        class="mx-report-page__bar"
        v-bind="mxActionBarProps"
        @finalize="mxFinalize"
        @jump-to-missing="mxJumpToMissing()"
      />

      <span
        class="ar-sr-only"
        aria-live="polite"
      >{{ mxReport.announce }}</span>
    </div>

    <!-- "Editar Compromisso" (aguardando horário): a mesma sidebar de edição da lista, montada só quando pedida. -->
    <new-appointment-sidebar
      v-if="editSidebarMounted"
      @hook:mounted="openEditSidebar"
    />
  </div>
</template>

<script>
// Report de compromisso — rota `appointment-report` (`/compromissos/:id/report`) — de TODOS os tipos: é para cá que
// a lista de Compromissos, a remoção depois de 24 h e a "Próxima pendência" levam (a antiga AppointmentReportSidebar
// não existe mais).
// Board "v4-report" da página "Evolução UI/UX (jornada)": cabeçalho (voltar, H1, chip do tipo, stepper), cartão do
// compromisso, cartão das perguntas com os selects revelados, barra fixa, e os estados carregando / aguardando
// horário / preenchendo (autosave) / finalizando / erro / já reportado / salvo.
//
// O formulário depende do tipo do compromisso, que só se conhece depois do GET do rascunho (feito pelo mixin
// `momentXReportForm`):
//  - Momento X → as perguntas do CX, com o estado e as regras do mixin (o mesmo do painel de Momento X da central);
//  - Entrevista, Parceria, Negociação e recrutamento de PN → `StandardReportForm` (as perguntas de sempre), montado
//    com a MESMA resposta do GET (`preloaded`), sem um segundo carregamento.
// Esta página só cuida do contexto: de onde veio (`?origem=pendencias` → "← Pendências", "Pendência N de M"), os
// próximos passos depois de finalizar e o "Cadastrar contrato" do Momento X fechado (nos tipos padrão o contrato da
// Parceria fechada é a pergunta de sempre, feita pelo próprio formulário).
import { mapGetters } from 'vuex'
import * as accountTypes from '@/modules/account/store/types'
import * as appointmentTypes from '@/modules/appointment/store/types'
import { isPendingAppointment } from '@/modules/account/store/report_lock'
import { dateToLocalYMD } from '@/helpers/converters'
import { APPOINTMENT, CONTRACT_RESOURCE, CREATE_ACTION } from '@/constants/resources'
import { X_MOMENT } from '@/constants/appointment_types'
import {
  EA,
  MXA,
  NA,
  PA,
} from '@/constants/appointment_status'
import {
  A11Y_COPY,
  APPOINTMENTS_ROUTE,
  APP_COPY,
  CLOSED_DEAL,
  FIGMA_COPY,
  HAD_MEETING,
  NEW_COPY,
  REPORT_CLASSIFICATION,
  REPORT_CLASSIFICATION_COPY,
  REPORT_ERROR_CODES,
  REPORT_HUB_ROUTE,
  REPORT_ORIGIN_PENDENCIES,
  REPORT_ORIGIN_QUERY,
  REPORT_ROUTE,
  appointmentTypeLabel,
} from '@/constants/appointment_report_v2'
import momentXReportForm, { MX_REPORT_STATUS } from '../mixins/momentXReportForm'
import { SR_REPORT_STATUS } from '../mixins/standardReportForm'
import { eventTypeTone } from '../helpers/report_ui'
// ReportSuccess antes dos demais, como na central de pendências: o CSS dos componentes compartilhados sai na
// mesma ordem nas duas páginas (senão o mini-css-extract acusa "Conflicting order" no chunk comum).
import ReportSuccess from '../components/ReportSuccess.vue'
import {
  ReportActionBar,
  ReportAppointmentCard,
  ReportDateLockBanner,
  ReportReasonSelect,
  ReportStepper,
  ReportYesNo,
} from '../components'
import StandardReportForm from '../components/StandardReportForm.vue'

// Lista de Compromissos pela rota de leitura (mesmo path `/compromissos` da rota do menu, `APPOINTMENTS_ROUTE`):
// o destino de quem abre o report sem poder criar compromissos.
const APPOINTMENT_LIST_ROUTE = 'appointment-list'
// Cadastro de contrato do negócio fechado (o destino do "Reportar agora" da antiga sidebar, `onContractReport`).
const CONTRACT_SAVE_ROUTE = 'contract-save'

// Status "Agendado" que ainda recebem report — os de `AppointmentReportPendencyService::PENDING_STATUSES`.
// Só a guarda do Momento X usa esta lista; a dos tipos padrão fica no StandardReportForm (inclui o recrutamento de PN).
const REPORTABLE_STATUSES = [EA, NA, PA, MXA]

// Recusas sem formulário do Momento X (422 do servidor ou detectadas ao abrir) → texto da tela.
const UNAVAILABLE_TEXTS = {
  [REPORT_ERROR_CODES.NOT_OWNER]: APP_COPY.notOwner,
  [REPORT_ERROR_CODES.NOT_REPORTABLE]: APP_COPY.notReportable,
}

// Estados do StandardReportForm que a página acompanha pelo evento `status-change`.
const STANDARD_DONE_STATUSES = [SR_REPORT_STATUS.SAVED, SR_REPORT_STATUS.ALREADY_REPORTED]
const STANDARD_BAR_STATUSES = [SR_REPORT_STATUS.EDITING, SR_REPORT_STATUS.ERROR, SR_REPORT_STATUS.FINALIZING]
const STANDARD_NO_STEPPER_STATUSES = [SR_REPORT_STATUS.UNAVAILABLE, SR_REPORT_STATUS.LOAD_ERROR]
// Falhas do GET feito pela página (sem compromisso na resposta): o aviso é o da página, para qualquer tipo.
const PAGE_FAILURE_STATUSES = [MX_REPORT_STATUS.LOAD_ERROR, MX_REPORT_STATUS.UNAVAILABLE]

const isMomentXType = eventType => Number(eventType) === X_MOMENT

// Enquanto a barra fixa está na tela, esta classe no <body> sobe o botão "voltar ao topo" do layout
// (3º bloco de estilo) — senão ele cobre o "Finalizar Momento X".
const BAR_BODY_CLASS = 'ar-report-bar-visible'

const SKELETON_ROWS = [1, 2, 3]

// Acima disso (como na central), o progresso da visita vira uma barra só, cheia na proporção das resolvidas.
const MAX_PENDENCY_SEGMENTS = 10

// "2026-09-30 14:07" no relógio LOCAL (sem toISOString/UTC) — formato que os helpers de data do report leem.
const localWallClock = date => `${dateToLocalYMD(date)} ${String(date.getHours()).padStart(2, '0')}:`
  + `${String(date.getMinutes()).padStart(2, '0')}`

const FORM_STATUSES = [
  MX_REPORT_STATUS.IDLE,
  MX_REPORT_STATUS.LOADING,
  MX_REPORT_STATUS.WAITING,
  MX_REPORT_STATUS.EDITING,
  MX_REPORT_STATUS.FINALIZING,
  MX_REPORT_STATUS.ERROR,
]

// Item da lista de GET /api/users/report_pendencies → `{ id, eventType }` (mais antiga primeiro).
const toPendencyItems = resp => {
  const rows = resp && resp.data && Array.isArray(resp.data.data) ? resp.data.data : []

  return rows
    .map(row => ({ id: Number(row && row.id), eventType: Number(row && row.event_type) }))
    .filter(item => Number.isInteger(item.id) && item.id > 0)
}

export default {
  name: 'MomentXReportPage',
  components: {
    NewAppointmentSidebar: () => import('@/modules/appointment/components/NewAppointmentSidebar.vue'),
    ReportActionBar,
    ReportAppointmentCard,
    ReportDateLockBanner,
    ReportReasonSelect,
    ReportStepper,
    ReportSuccess,
    ReportYesNo,
    StandardReportForm,
  },
  mixins: [momentXReportForm],
  props: {
    id: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      A11Y_COPY,
      APP_COPY,
      FIGMA_COPY,
      NEW_COPY,
      REPORT_CLASSIFICATION,
      REPORT_CLASSIFICATION_COPY,
      SKELETON_ROWS,
      // Aberto pela central (ou pela trava, num compromisso pendente): fixado ao abrir, para a tela não trocar
      // de contexto quando a trava for liberada ao finalizar.
      pendencyContext: false,
      // Pendências desta visita, da mais antiga para a mais nova: a lista do servidor ao abrir, mantida ao seguir
      // para a "Próxima pendência" (a mesma tela com outro id) — as resolvidas no caminho continuam contando no
      // "Pendência N de M" e no progresso.
      pendencyItems: [],
      // Ids da última consulta às pendências (null = ainda não consultado). Item da visita fora dela = resolvido.
      pendingIdsNow: null,
      // O acesso esteve travado nesta visita: só então o fim das pendências é "Acesso liberado" + "Continuar".
      lockSeen: false,
      // Horário local em que o finalize respondeu (quando o resumo do servidor não traz `created_at`).
      savedLocalAt: '',
      editSidebarMounted: false,
      // Tipos padrão: a resposta do GET do rascunho que monta o StandardReportForm (`key` novo a cada resposta — um
      // recarregamento, ex. depois de "Editar Compromisso", remonta o formulário com os dados novos).
      standardSeed: null,
      // Estado do StandardReportForm (`SR_REPORT_STATUS`, pelo evento `status-change`) e o `pending_count` do finalize.
      standardStatus: '',
      standardPendingCount: null,
    }
  },
  computed: {
    ...mapGetters({
      user: accountTypes.USER,
      reportLock: accountTypes.REPORT_LOCK,
      isReportLocked: accountTypes.IS_REPORT_LOCKED,
      newAppointmentSidebar: appointmentTypes.NEW_APPOINTMENT_SIDEBAR,
    }),
    appointmentId() {
      return Number(this.id)
    },
    fromHub() {
      return this.$route.query[REPORT_ORIGIN_QUERY] === REPORT_ORIGIN_PENDENCIES
    },
    canCreateAppointment() {
      return this.$can(CREATE_ACTION, APPOINTMENT)
    },
    canCreateContract() {
      return this.$can(CREATE_ACTION, CONTRACT_RESOURCE)
    },

    // ---- Estados da tela ----
    isLoadingView() {
      return this.mxIsLoading || this.mxStatus === MX_REPORT_STATUS.IDLE
    },
    // Tipo do compromisso carregado (0 enquanto não se sabe: carregando, ou o GET falhou).
    loadedEventType() {
      const { appointment } = this.mxReport
      return Number(appointment && appointment.event_type) || 0
    },
    // Entrevista, Parceria, Negociação ou recrutamento de PN: o formulário é o StandardReportForm. Continua valendo
    // durante um recarregamento (o GET mantém o compromisso), para o formulário não sumir e voltar.
    standardMode() {
      return this.loadedEventType > 0 && !isMomentXType(this.loadedEventType)
        && !PAGE_FAILURE_STATUSES.includes(this.mxStatus)
    },
    // O GET do rascunho abre qualquer compromisso do PN (só salvar e finalizar recusam o status, com 422): um
    // Momento X que saiu do funil sem report já abre sem o formulário — senão ele apareceria e sumiria no primeiro
    // autosave. (Nos tipos padrão, a mesma guarda é do StandardReportForm.)
    blockedCode() {
      const { appointment, report } = this.mxReport
      if (!appointment || this.isLoadingView || this.standardMode) return ''
      if (report || !FORM_STATUSES.includes(this.mxStatus)) return ''
      const statusId = Number(appointment.status_id)
      if (statusId > 0 && !REPORTABLE_STATUSES.includes(statusId)) return REPORT_ERROR_CODES.NOT_REPORTABLE

      return ''
    },
    showForm() {
      return !this.standardMode && !this.blockedCode && FORM_STATUSES.includes(this.mxStatus)
    },
    mxShowDone() {
      return !this.standardMode && !this.blockedCode && this.mxIsDone
    },
    standardDone() {
      return this.standardMode && STANDARD_DONE_STATUSES.includes(this.standardStatus)
    },
    // Salvo ou já reportado, em qualquer formulário.
    isDone() {
      return this.mxShowDone || this.standardDone
    },
    showUnavailable() {
      if (this.standardMode) return false
      return !!this.blockedCode || this.mxStatus === MX_REPORT_STATUS.UNAVAILABLE
    },
    showLoadError() {
      return !this.standardMode && this.mxStatus === MX_REPORT_STATUS.LOAD_ERROR
    },
    // Chip do tipo ao lado do H1 — só com o tipo conhecido (a página abre qualquer tipo; enquanto carrega, ou se o
    // GET falhou, não há o que afirmar).
    typeChip() {
      const eventType = this.loadedEventType
      if (!eventType || this.showLoadError) return null
      const { appointment } = this.mxReport
      const label = appointmentTypeLabel(eventType) || String((appointment && appointment.event_type_description) || '')
      if (!label) return null

      return { label, tone: eventTypeTone(eventType) }
    },
    // Sem formulário (indisponível / falha ao carregar), a etapa "Report de compromisso" não está em andamento.
    showStepper() {
      if (this.standardMode) return !STANDARD_NO_STEPPER_STATUSES.includes(this.standardStatus)
      return !this.showUnavailable && !this.showLoadError
    },
    stepperState() {
      if (!this.standardMode) return this.mxStepperState
      if (this.standardDone) return 'done'
      return this.standardStatus === SR_REPORT_STATUS.WAITING ? 'locked' : 'active'
    },
    // Barra fixa do Momento X (a dos tipos padrão é do StandardReportForm).
    showActionBar() {
      return this.showForm && this.mxShowActionBar
    },
    // Alguma barra fixa na tela (sobe o "voltar ao topo" e os botões fixos do layout).
    barVisible() {
      if (this.standardMode) return STANDARD_BAR_STATUSES.includes(this.standardStatus)
      return this.showActionBar
    },
    isSaved() {
      return !this.standardMode && this.mxStatus === MX_REPORT_STATUS.SAVED
    },
    // "Editar Compromisso" no aguardando horário dos tipos padrão (o formulário só mostra o botão se alguém ouvir).
    standardEditListeners() {
      return this.canCreateAppointment ? { 'edit-appointment': this.openEditAppointment } : {}
    },
    unavailableText() {
      if (this.blockedCode) return UNAVAILABLE_TEXTS[this.blockedCode]

      return this.mxReport.errorMessage
        || UNAVAILABLE_TEXTS[this.mxReport.unavailableCode]
        || APP_COPY.loadReportError
    },
    successNotice() {
      return this.mxStatus === MX_REPORT_STATUS.ALREADY_REPORTED ? APP_COPY.alreadyReported : ''
    },
    // "Finalizado por … em … às …": vem do resumo do servidor (`reported_by`, `created_at`). Se a resposta do
    // finalize vier sem eles, quem finalizou nesta tela é o usuário logado, no horário local da resposta. Um
    // report antigo (já reportado, sem `reported_by`) fica sem a linha — nada é presumido.
    successReportedBy() {
      const { report } = this.mxReport
      if (!this.isSaved || (report && report.reported_by && report.reported_by.name)) return ''
      return this.user && this.user.name ? String(this.user.name) : ''
    },
    successReportedAt() {
      const { report } = this.mxReport
      if (!this.isSaved || (report && report.created_at)) return ''
      return this.savedLocalAt
    },

    // ---- Navegação ----
    // A tela "Compromissos" do menu (o item fica marcado) para quem pode abri-la; senão a mesma lista pela rota
    // de leitura.
    appointmentsTo() {
      return { name: this.canCreateAppointment ? APPOINTMENTS_ROUTE : APPOINTMENT_LIST_ROUTE }
    },
    hubTo() {
      return { name: REPORT_HUB_ROUTE }
    },
    backTo() {
      return this.pendencyContext ? this.hubTo : this.appointmentsTo
    },
    backLabel() {
      return this.pendencyContext ? NEW_COPY.pendenciesTitle : APP_COPY.appointments
    },
    // Acesso liberado agora: o botão final é o "Continuar" do app; senão, "Voltar para compromissos".
    leaveLabel() {
      return this.showReleasedCallout ? APP_COPY.continue : NEW_COPY.backToAppointments
    },
    dateLockListeners() {
      const listeners = { released: this.mxOnReleased }
      if (this.canCreateAppointment) listeners.edit = this.openEditAppointment
      return listeners
    },
    editSidebarVisible() {
      return !!(this.newAppointmentSidebar && this.newAppointmentSidebar.visible)
    },

    // ---- "Pendência N de M" ----
    pendencyIndex() {
      return this.pendencyItems.findIndex(item => item.id === this.appointmentId)
    },
    showPendencyBar() {
      return this.pendencyContext && this.pendencyIndex >= 0
    },
    // Um segmento por pendência da visita: resolvida (verde), esta (laranja; verde ao finalizar) ou a fazer.
    // Mais de MAX_PENDENCY_SEGMENTS: uma barra só com `ratio` (resolvidas / total).
    pendencySegments() {
      const pendingNow = this.pendingIdsNow
      const segments = this.pendencyItems.map(item => {
        let state = 'todo'
        if (item.id === this.appointmentId) state = this.isDone ? 'done' : 'current'
        else if (pendingNow && !pendingNow.includes(item.id)) state = 'done'
        return { key: item.id, state }
      })
      if (segments.length <= MAX_PENDENCY_SEGMENTS) return segments
      const done = segments.filter(segment => segment.state === 'done').length

      return [{ key: 'ratio', state: 'ratio', ratio: done / segments.length }]
    },
    // Pendências que ainda restam (sem este compromisso), na ordem da lista; null enquanto não consultadas.
    remainingItems() {
      const pendingNow = this.pendingIdsNow
      if (!pendingNow) return null

      return this.pendencyItems.filter(item => item.id !== this.appointmentId && pendingNow.includes(item.id))
    },
    remainingPendencies() {
      const pendingCount = this.standardMode ? this.standardPendingCount : this.mxReport.pendingCount
      if (Number.isInteger(pendingCount)) return pendingCount
      if (this.remainingItems) return this.remainingItems.length
      return (this.reportLock && this.reportLock.count) || 0
    },
    // A mais antiga que resta, de qualquer tipo: direto no report dela, no mesmo contexto (esta página abre todos os
    // tipos). Lista ainda não consultada → a central.
    nextPendencyTo() {
      const [next] = this.remainingItems || []
      if (next) {
        return {
          name: REPORT_ROUTE,
          params: { id: String(next.id) },
          query: { [REPORT_ORIGIN_QUERY]: REPORT_ORIGIN_PENDENCIES },
        }
      }
      return this.hubTo
    },

    // ---- Próximos passos depois de salvar ----
    showPendencySteps() {
      return this.remainingPendencies > 0 && (this.pendencyContext || this.isReportLocked)
    },
    // Só quando esta visita começou (ou passou) pela trava e ela acabou de sair — não para quem nunca esteve
    // travado nem ao reabrir, depois, um report já feito.
    showReleasedCallout() {
      return this.pendencyContext && this.lockSeen && !this.isReportLocked && this.remainingPendencies === 0
    },
    // Momento X fechado (nos tipos padrão, o contrato da Parceria é a pergunta do próprio formulário).
    savedClosedDeal() {
      if (!this.isSaved) return false
      const { report, answers } = this.mxReport
      if (report && typeof report.closed_deal === 'boolean') {
        return report.had_meeting !== false && report.closed_deal === true
      }
      return answers[HAD_MEETING] === true && answers[CLOSED_DEAL] === true
    },
    // Negócio fechado: o cadastro do contrato só fica para depois quando ainda há pendências E a trava as segura (o
    // `contract-save` fica atrás dela). Com a trava em `log`/`off` nada está bloqueado: "Cadastrar contrato" aparece
    // mesmo com pendências restantes.
    contractHeldByLock() {
      return this.remainingPendencies > 0 && this.isReportLocked
    },
    canRegisterContract() {
      return this.canCreateContract && this.savedClosedDeal && !this.contractHeldByLock
    },
    contractDeferred() {
      return this.canCreateContract && this.savedClosedDeal && this.contractHeldByLock
    },
  },
  watch: {
    // "Próxima pendência" reaproveita esta página com outro id.
    id() {
      this.initPage({ switching: true })
      this.$nextTick(() => this.focusTitle())
    },
    barVisible: {
      immediate: true,
      handler: 'syncBodyBarClass',
    },
    editSidebarVisible(visible, wasVisible) {
      if (wasVisible && !visible && this.$_editSidebarOpened) this.releaseEditSidebar()
    },
    isReportLocked(locked) {
      if (locked) this.lockSeen = true
    },
  },
  created() {
    this.$_pageGeneration = 0
    this.$_pendencySeq = 0
    this.$_standardSeedCount = 0
    this.$_pageDestroyed = false
    this.$_editSidebarOpened = false
    this.initPage()
  },
  beforeDestroy() {
    this.$_pageDestroyed = true
    document.body.classList.remove(BAR_BODY_CLASS)
    if (this.$_editSidebarOpened) this.releaseEditSidebar()
  },
  methods: {
    initPage(options) {
      const switching = !!(options && options.switching)
      this.$_pageGeneration += 1
      const context = this.fromHub
        || (this.isReportLocked && isPendingAppointment(this.reportLock, this.appointmentId))
      // Seguiu para a próxima pendência da mesma lista: a visita continua (o progresso não recomeça do zero).
      const continuing = switching && context && this.pendencyContext
        && this.pendencyItems.some(item => item.id === this.appointmentId)
      this.pendencyContext = context
      if (!continuing) {
        this.pendencyItems = []
        this.pendingIdsNow = null
        this.lockSeen = false
      }
      if (this.isReportLocked) this.lockSeen = true
      this.savedLocalAt = ''
      // O formulário dos tipos padrão é do compromisso anterior: sai até o GET deste dizer o tipo.
      this.standardSeed = null
      this.standardStatus = ''
      this.standardPendingCount = null
      this.mxInit(this.id)
      if (context) this.loadPendencyItems()
    },

    /**
     * Toda resposta do GET do rascunho passa por aqui (abrir, "Tentar novamente", recarregar depois de "Editar
     * Compromisso"). O mixin do Momento X segue aplicando-a como sempre; se o compromisso é de outro tipo, a mesma
     * resposta monta (ou remonta) o StandardReportForm — o mixin do Momento X não chega a mostrar nada para ele.
     */
    mxApplyShowResponse(data) {
      const body = data || {}
      const eventType = Number(body.appointment && body.appointment.event_type) || 0
      this.$_standardSeedCount += 1
      this.standardSeed = eventType > 0 && !isMomentXType(eventType)
        ? { key: `${this.appointmentId}-${this.$_standardSeedCount}`, body }
        : null

      return momentXReportForm.methods.mxApplyShowResponse.call(this, data)
    },

    // Lista completa das pendências (também atualiza a trava no store). Resolve com os itens ou null; nunca
    // rejeita, e ignora a resposta se a tela já mudou de compromisso.
    fetchPendencyItems() {
      const generation = this.$_pageGeneration
      return this.$store.dispatch(appointmentTypes.GET_REPORT_PENDENCIES)
        .then(resp => (generation === this.$_pageGeneration && !this.$_pageDestroyed ? toPendencyItems(resp) : null))
        .catch(() => null)
    },

    // Junta a lista do servidor à da visita: pendência nova (ex.: virou o dia) entra no fim — a lista vai da mais
    // antiga para a mais nova.
    applyPendencyItems(items) {
      const knownIds = this.pendencyItems.map(item => item.id)
      this.pendencyItems = this.pendencyItems.concat(items.filter(item => !knownIds.includes(item.id)))
      this.pendingIdsNow = items.map(item => item.id)
    },

    // Só a consulta mais recente vale: a do abrir pode voltar depois da do finalize e traria a lista de antes.
    loadPendencyItems() {
      this.$_pendencySeq += 1
      const sequence = this.$_pendencySeq
      return this.fetchPendencyItems().then(items => {
        if (items && sequence === this.$_pendencySeq) this.applyPendencyItems(items)
      })
    },

    // Gancho do mixin: report finalizado (`{ report, replayed, pending_count }`). Com pendências restantes,
    // busca a lista para a "Próxima pendência" e para o progresso.
    onMomentXReportFinalized(data) {
      this.savedLocalAt = localWallClock(new Date())
      const pendingCount = Number(data && data.pending_count)
      if (Number.isInteger(pendingCount) && pendingCount === 0) {
        // Nada mais pendente: todas as da visita estão resolvidas (sem nova consulta).
        this.pendingIdsNow = []
        return
      }
      this.loadPendencyItems()
    },

    // Gancho do mixin: o compromisso já tinha report — ao abrir (a lista da visita já está sendo buscada) ou
    // num 409 do finalize (feito em outra aba). No 409 a lista da visita está velha: busca de novo para o
    // "Faltam N pendências" e a "Próxima pendência".
    onMomentXReportAlreadyReported() {
      // Tipo padrão já reportado: quem cuida é o StandardReportForm (`onStandardAlreadyReported`).
      if (this.standardMode) return
      if (this.pendingIdsNow !== null) this.loadPendencyItems()
    },

    // ---- Tipos padrão (StandardReportForm) ----
    onStandardStatusChange(status) {
      this.standardStatus = status || ''
    },

    // Report gravado: as mesmas regras do Momento X para a "Próxima pendência" e o progresso. A trava já foi
    // atualizada pela action FINALIZE_STANDARD_REPORT (libera o compromisso + REFRESH_REPORT_LOCK forçado).
    onStandardReported(payload) {
      const pendingCount = payload ? payload.pendingCount : null
      this.standardPendingCount = Number.isInteger(pendingCount) ? pendingCount : null
      if (this.standardPendingCount === 0) {
        this.pendingIdsNow = []
        return
      }
      this.loadPendencyItems()
    },

    // Já tinha report (ao abrir, ou 409 no finalize): a lista da visita pode estar velha (ver o gancho do MX).
    onStandardAlreadyReported() {
      if (this.pendingIdsNow !== null) this.loadPendencyItems()
    },

    // O mesmo destino do antigo "Reportar agora" da sidebar (`onContractReport`): contract-save com o compromisso.
    registerContract() {
      this.$router.push({
        name: CONTRACT_SAVE_ROUTE,
        params: {
          blocked: false,
          appointment: this.appointmentId,
        },
      }).catch(() => {})
    },

    focusTitle() {
      const { pageTitle } = this.$refs
      if (pageTitle && typeof pageTitle.focus === 'function') pageTitle.focus({ preventScroll: true })
    },

    syncBodyBarClass() {
      document.body.classList.toggle(BAR_BODY_CLASS, !!this.barVisible && !this.$_pageDestroyed)
    },

    // "Editar Compromisso" no aguardando horário: a sidebar de edição da lista de Compromissos. Depois de salvar,
    // recarrega (o novo horário muda a contagem regressiva).
    openEditAppointment() {
      if (!this.editSidebarMounted) {
        // Monta a sidebar (import sob demanda); `openEditSidebar` roda no `hook:mounted` dela.
        this.editSidebarMounted = true
        return
      }
      this.openEditSidebar()
    },
    openEditSidebar() {
      this.$_editSidebarOpened = true
      this.$store.dispatch(appointmentTypes.OPEN_NEW_APPOINTMENT_SIDEBAR, {
        id: this.appointmentId,
        saveAction: () => this.mxLoad(),
      })
    },
    // O estado da sidebar é global e as outras telas a abrem só com `customer_id`: sem limpar o `id`, a próxima
    // abertura (ex.: agendar pela lista de Clientes) viria como edição deste compromisso.
    releaseEditSidebar() {
      this.$_editSidebarOpened = false
      this.$store.commit(appointmentTypes.MUTATE_NEW_APPOINTMENT_SIDEBAR, {
        visible: false,
        id: undefined,
        saveAction: undefined,
      })
    },
  },
}
</script>

<style lang="scss" src="@/assets/scss/_appointment-report.scss"></style>

<style lang="scss" scoped>
// Gutter do conteúdo do Oráculo (Vuexy `$content-padding` = 2rem; 1.2rem em telas xs): a barra fixa se estende
// até as bordas da coluna de conteúdo (nunca por cima do menu) e alinha o miolo com a coluna de 920px.
.mx-report-page {
  --ar-page-gutter: 2rem;
  // Altura útil da área de conteúdo (padding do topo com a navbar flutuante + rodapé estático): com pouco
  // conteúdo a barra continua no pé da tela, como no board.
  min-height: calc(100vh - 11.1rem);
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.mx-report-page--done {
  padding-bottom: 40px;
}

.mx-report-page__column {
  width: 100%;
  max-width: var(--ar-content-max);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.mx-report-page > .mx-report-page__bar {
  --ar-bar-gutter: var(--ar-page-gutter);
  margin: auto calc(-1 * var(--ar-page-gutter)) 0;
}

// ---- Tipos padrão: o StandardReportForm ocupa o resto da página (a barra dele fica no pé, como a do Momento X) e
// o conteúdo volta para a coluna de 920px. -4px: entre o cabeçalho e o cartão fica o mesmo espaço da coluna (20px),
// não o da página (24px).
.mx-report-page__standard {
  flex: 1 1 auto;
  margin-top: -4px;
}

.mx-report-page__standard ::v-deep .ar-srf__body {
  width: 100%;
  max-width: var(--ar-content-max);
  margin: 0 auto;
}

.mx-report-page__standard ::v-deep .mx-report-page__standard-bar {
  --ar-bar-gutter: var(--ar-page-gutter);
  margin: auto calc(-1 * var(--ar-page-gutter)) 0;
}

// ---- Cabeçalho ----
.mx-report-page__header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px 24px;
}

.mx-report-page__heading {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.mx-report-page__back {
  align-self: flex-start;
}

.mx-report-page__title-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
}

.mx-report-page__title {
  margin: 0;
  font-size: 24px;
  line-height: 32px;
  font-weight: 600;
  letter-spacing: -.2px;
  color: var(--ar-text-1);
}

.mx-report-page__header .mx-report-page__stepper {
  margin: 0 0 4px;
}

// ---- Contexto da central ----
.mx-report-page__context {
  padding: 12px 16px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 14px;
  border-radius: var(--ar-radius-card);
  background: var(--ar-brand-tint-soft);
  border: 1px solid var(--ar-brand-border-soft);
}

.mx-report-page__context-icon {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--ar-radius-pill);
  background: var(--ar-brand-tint);
  color: var(--ar-brand-text);
}

.mx-report-page__context-position {
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  color: var(--ar-text-1);
  white-space: nowrap;
}

.mx-report-page__segments {
  min-width: 0;
  flex: 0 1 auto;
}

// Muitas pendências: os segmentos encolhem em vez de estourar a linha.
.mx-report-page__segments .ar-progress__seg {
  width: auto;
  min-width: 8px;
  flex: 0 1 40px;
}

// Mais de 10 pendências: uma barra só, cheia (verde) na proporção das resolvidas.
.mx-report-page__segments .mx-report-page__seg-ratio {
  min-width: 120px;
  flex: 0 1 240px;
}

.mx-report-page__segments .mx-report-page__seg-ratio::after {
  background: var(--ar-success);
  transform: scaleX(var(--mx-fill, 0));
}

.mx-report-page__context-caption {
  margin-left: auto;
  font-size: 13px;
  line-height: 20px;
  font-weight: 500;
  color: var(--ar-text-2);
  text-align: right;
}

// ---- Carregando (sem animação em loop) ----
.mx-report-page__skeleton-row {
  min-height: 72px;
  padding: 16px 0;
  display: flex;
  align-items: center;
  gap: 16px;
}

.mx-report-page__skeleton-row + .mx-report-page__skeleton-row {
  border-top: 1px solid var(--ar-divider);
}

.mx-report-page__skeleton-legend {
  flex: 0 1 280px;
  height: 14px;
  border-radius: var(--ar-radius-thumb);
  background: var(--ar-bg-raised);
}

.mx-report-page__skeleton-control {
  width: 200px;
  height: 40px;
  margin-left: auto;
  flex-shrink: 0;
  border-radius: var(--ar-radius-control);
  border: 1px solid var(--ar-divider);
  background: var(--ar-bg-field);
}

// ---- Indisponível / falha ao carregar ----
.mx-report-page__notice {
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.mx-report-page__notice-text {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
}

.mx-report-page__notice-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

// ---- Próximos passos (MX Salvo) ----
.mx-report-page__next-callout {
  width: 100%;
}

.mx-report-page__callout-title {
  font-weight: 600;
}

.mx-report-page__next-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.mx-report-page__next-primary {
  min-width: 220px;
}

.mx-report-page__chevron {
  transition: transform var(--ar-dur-fast) var(--ar-ease-standard);
}

.mx-report-page__chevron--open {
  transform: rotate(180deg);
}

@media (max-width: 767.98px) {
  .mx-report-page__header {
    align-items: flex-start;
  }

  .mx-report-page__context-caption {
    flex-basis: 100%;
    margin-left: 0;
    text-align: left;
  }
}

@media (max-width: 575.98px) {
  .mx-report-page {
    --ar-page-gutter: 1.2rem;
    min-height: calc(100vh - 10.3rem);
  }

  .mx-report-page__notice {
    padding: 16px;
  }

  .mx-report-page__skeleton-row {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .mx-report-page__skeleton-control {
    width: 100%;
    height: 48px;
    margin-left: 0;
  }

  .mx-report-page__next-actions {
    flex-direction: column;
    align-items: stretch;

    .ar-btn {
      width: 100%;
      height: 48px;
    }
  }

  .mx-report-page__next-primary {
    min-width: 0;
  }
}
</style>

<style lang="scss">
// Barra fixa do Report no rodapé da tela (classe no <body> só enquanto ela aparece): o botão "voltar ao topo" do
// layout, fixo no canto inferior direito e acima de tudo, sobe para não cobrir o "Finalizar Momento X".
body.ar-report-bar-visible .btn-scroll-to-top {
  bottom: 88px;
}

// Os botões fixos "Atendimento online" / "Feedback de erro" (App.vue, canto inferior esquerdo, acima de tudo) nunca
// cobrem a barra: com o menu aberto (>= 1200px) sobem para cima dela; abaixo disso o menu fica sobreposto, o conteúdo
// começa na borda e eles cobririam o status, o banner de erro e o CTA — somem enquanto a barra aparece (como no
// modal de novidades).
body.ar-report-bar-visible .float-bottom-left {
  bottom: 88px;
}

body.ar-report-bar-visible .float-bottom-left + .float-bottom-left {
  bottom: 142px;
}

@media (max-width: 1199.98px) {
  body.ar-report-bar-visible .float-bottom-left {
    display: none;
  }
}

// Telas estreitas: a barra quebra em duas linhas (status em cima, CTA de 48px embaixo).
@media (max-width: 575.98px) {
  body.ar-report-bar-visible .btn-scroll-to-top {
    bottom: 140px;
  }
}
</style>
